import { Alert, Button, Divider, Flex, message, Spin, Switch, Table, Tag, Typography } from 'antd';
import { EditOutlined, ExportOutlined, WarningOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useWindowSize } from '@leedpulse-monorepo/hooks';
import React, { useContext, useMemo, useState } from 'react';
import { IStripeProducts, IStripeProduct, ISmallPlan } from '../../../domain/models/stripe/Stripe';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import updatePlanBetter from '../../../domain/use_cases/plans/updatePlanBetter';
import { PLANS_PAGE_PATH } from '../../pages/plans/PlansPage';
import { advancedOnRow } from '../../../core/utils/commonUtils';

interface LeedflowStripeProductsListProps {
  stripeProducts: IStripeProducts | null;
  loading: boolean;
  expandAllRows?: boolean;
  orphanAlertClosed?: boolean;
  onOrphanAlertClose?: () => void;
}

const warningLabel = (warning: ISmallPlan['warning']): string | null => {
  if (warning === 'inactive_price') return 'Stripe price inactive';
  return null;
};

const LeedflowStripeProductsList: React.FC<LeedflowStripeProductsListProps> = ({ stripeProducts, loading, expandAllRows = false, orphanAlertClosed = false, onOrphanAlertClose }) => {
  const windowSize = useWindowSize();
  const navigate = useNavigate();

  const authContext = useContext(AuthenticationContext);

  const isWide = windowSize.width > 576;

  const [localStripeProducts, setLocalStripeProducts] = React.useState<IStripeProducts | null>(null);

  const products = useMemo(() => localStripeProducts?.products ?? [], [localStripeProducts]);
  const orphanPlans = useMemo(() => localStripeProducts?.orphan_plans ?? [], [localStripeProducts]);

  const handlePlanActiveChange = (planId: string, active: boolean) => {
    if (!authContext.authentication) {
      message.error('Vous devez être authentifié pour modifier l\'activité d\'un plan');
      return;
    }

    updatePlanState(planId, { active });
    updatePlanBetter(planId, { active }, authContext.authentication)
      .then(() => {
        message.success('Activité du plan modifiée avec succès');
      }).catch(() => {
        message.error('Erreur lors de la modification de l\'activité du plan');
      }).finally(() => {
        updatePlanState(planId, { active });
      });
  };

  const updatePlanState = (planId: string, body: Partial<ISmallPlan>) => {
    setLocalStripeProducts((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        products: prev.products.map((product) => ({
          ...product,
          prices: product.prices.map((price) => ({
            ...price,
            plans: price.plans.map((plan) =>
              plan.id === planId ? { ...plan, ...body } : plan
            ),
          })),
        })),
      };
    });
  };

  const handleEditPlan = (planId: string, openInNewTab = false) => {
    if (openInNewTab) {
      window.open(`${PLANS_PAGE_PATH}/${planId}`, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(`${PLANS_PAGE_PATH}/${planId}`);
  };

  // Ajuste pendant le rendu (pattern « ajuster l'etat quand une prop change » de
  // la doc React) : dans un effet, ce recalcul coutait un second rendu.
  const [lastStripeProducts, setLastStripeProducts] = useState(stripeProducts);
  if (stripeProducts !== lastStripeProducts) {
    setLastStripeProducts(stripeProducts);
    setLocalStripeProducts(stripeProducts);
  }

  return (
    localStripeProducts && !loading ?
      <>
        {localStripeProducts && orphanPlans.length > 0 && !orphanAlertClosed ? (
          <Alert
            style={{ marginBottom: 24 }}
            type="warning"
            showIcon
            message="Plans orphelins"
            closable
            onClose={onOrphanAlertClose}
            description={
              <>
                <Typography.Paragraph style={{ marginBottom: 12 }}>
                  Ces plans ne sont pas reliés à un price Stripe valide.
                </Typography.Paragraph>
                <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                  {orphanPlans.map((o, i) => (
                    <React.Fragment key={i}>
                      <li style={{ marginBottom: 12 }}>
                        <Flex justify="space-between" align="flex-start" gap={16} wrap="wrap">
                          <div>
                            <Typography.Text strong>{o.name}</Typography.Text>{' '}
                            <Typography.Text type="secondary">({o.id})</Typography.Text>
                            <div style={{ marginTop: 4 }}>
                              {o.issue === 'missing_price' ? (
                                <Typography.Text type="secondary">
                                  Aucun <code>stripe_price_id</code> sur le plan.
                                </Typography.Text>
                              ) : (
                                <Typography.Text type="secondary">
                                  Price inconnu ou supprimé : <code>{o.stripe_price_id}</code>
                                </Typography.Text>
                              )}
                            </div>
                          </div>
                          <Button
                            type="primary"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEditPlan(o.id, true)}
                          >
                            Modifier le plan
                          </Button>
                        </Flex>
                      </li>
                      {i < orphanPlans.length - 1 && <Divider size='small' />}
                    </React.Fragment>
                  ))}
                </ul>
              </>
            }
          />
        ) : null}

        <Table<IStripeProduct>
          key={expandAllRows ? 'expanded' : 'collapsed'}
          pagination={false}
          loading={loading}
          rowKey="id"
          dataSource={products}
          columns={
            isWide
              ? [
                {
                  title: 'Produit Stripe',
                  dataIndex: 'name',
                  key: 'name',
                  render: (name: string, row) => (
                    <Flex vertical gap={4}>
                      <Typography.Text strong>{name || '—'}</Typography.Text>
                      {row.description ? (
                        <Typography.Text type="secondary" ellipsis style={{ maxWidth: 480 }}>
                          {row.description}
                        </Typography.Text>
                      ) : null}
                    </Flex>
                  ),
                },
                {
                  title: 'Actif',
                  dataIndex: 'active',
                  key: 'active',
                  width: 90,
                  render: (active: boolean) => (active ? 'Oui' : 'Non'),
                },
                {
                  title: 'Plans reliés',
                  dataIndex: 'plans_count',
                  key: 'plans_count',
                  width: 120,
                },
                {
                  title: '',
                  key: 'product_stripe',
                  width: 160,
                  render: (_, row) => (
                    <Button
                      href={row.dashboard_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      icon={<ExportOutlined />}
                      onClick={(e) => e.stopPropagation()}
                    >

                    </Button>
                  ),
                },
              ]
              : [
                {
                  title: 'Produit',
                  dataIndex: 'name',
                  key: 'name',
                },
              ]
          }
          expandable={{
            defaultExpandAllRows: expandAllRows,
            expandRowByClick: true,
            expandedRowRender: (product) => {
              const rows = product.prices.flatMap((price) =>
                price.plans.map((plan) => ({ ...plan, price }))
              );

              if (rows.length === 0) {
                return (
                  <Typography.Text type="secondary" style={{ paddingLeft: 8 }}>
                    Aucun plan lié à un tarif Stripe de ce produit Stripe.
                  </Typography.Text>
                );
              }

              return (
                <Table
                  size="small"
                  showHeader={false}
                  pagination={false}
                  rowKey={(r) => `${r.price.id}-${r.id}`}
                  dataSource={rows}
                  columns={
                    isWide
                      ? [
                        {
                          title: 'Actif',
                          dataIndex: 'active',
                          key: 'active',
                          width: 60,
                          render: (active: boolean, row) => (
                            <Switch
                              checked={active}
                              onChange={(checked, e) => {
                                e.stopPropagation();
                                handlePlanActiveChange(row.id, checked);
                              }}
                            />
                          ),
                        },
                        {
                          title: 'Plan',
                          dataIndex: 'name',
                          key: 'name',
                          render: (name: string, row) => (
                            <Flex align="center" gap={8} wrap="wrap">
                              <span>{name || '—'}</span>
                              {row.archived ? <Tag color="default">Archivé</Tag> : null}
                              {row.warning ? (
                                <Tag icon={<WarningOutlined />} color="warning">
                                  {warningLabel(row.warning)}
                                </Tag>
                              ) : null}
                            </Flex>
                          ),
                        },
                        {
                          title: 'Prix (Stripe)',
                          key: 'amount',
                          width: 140,
                          render: (_, row) => row.price.formatted_amount,
                        },
                        {
                          title: 'ID tarif Stripe',
                          dataIndex: ['price', 'id'],
                          key: 'price_id',
                          ellipsis: true,
                          render: (_, row) => (
                            <Typography.Text copyable={{ text: row.price.id }} ellipsis>
                              {row.price.id}
                            </Typography.Text>
                          ),
                        },
                        {
                          title: 'Actif',
                          key: 'price_active',
                          width: 100,
                          render: (_, row) => (row.price.active ? 'Oui' : 'Non'),
                        },
                        {
                          title: '',
                          key: 'open_stripe',
                          width: 200,
                          render: (_, row) => (
                            <Flex gap={8}>
                              <Button
                                type="primary"
                                size="small"
                                href={row.price.dashboard_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                icon={<ExportOutlined />}
                                onClick={(e) => e.stopPropagation()}
                              >
                              </Button>
                              <Button
                                type="primary"
                                size="small"
                                href={`${PLANS_PAGE_PATH}/${row.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                icon={<EditOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleEditPlan(row.id);
                                }}
                              ></Button>
                            </Flex>

                          ),
                        },
                      ]
                      : [
                        {
                          title: 'Plan',
                          dataIndex: 'name',
                          key: 'name',
                        },
                      ]
                  }
                  onRow={(record) =>
                    advancedOnRow({
                      url: `${PLANS_PAGE_PATH}/${record.id}`,
                      navigate,
                      className: 'table-row',
                    })
                  }
                />
              );
            },
          }}
        />
      </>
      :
      (
        <Flex
          justify="center"
          align="center"
        >
          <Spin />
        </Flex>
      )
  );
};

export default LeedflowStripeProductsList;
